import React, { useMemo, useState } from "react";
import "./cpe-progress.css";
import {
  certificateFileUrl,
  emailCertificate,
} from "../../services/certificate/certificate-api";
import { useAttendedMinutes } from "./use-attended-minutes";
import { useBackendProgress } from "./use-backend-progress";

export { useAttendedMinutes };

export type CpeProgressType = "cpe" | "shrm";

export interface CpeProgressPollMarker {
  pollId: string;
  sequence: number;
  /** Position on the timeline, 0..1. If omitted, evenly spaced. */
  offsetFraction?: number;
  answered: boolean;
  missed?: boolean;
}

export interface CpeProgressWidgetProps {
  /** "cpe" or "shrm" — controls the badge text. */
  certificateType?: CpeProgressType;
  /** Whole minutes already attended. */
  attendedMinutes: number;
  /** Minutes required to be eligible. */
  requiredMinutes: number;
  /** Poll markers; rendered along the timeline. */
  pollMarkers?: CpeProgressPollMarker[];
  /** How many polls the attendee must answer to be eligible. */
  pollsRequired?: number;
  /** Whether the meeting is currently live. Drives the playhead. */
  isLive?: boolean;
  /** Eligibility result. If undefined, banner shows the in-progress state. */
  eligible?: boolean;
  /** Credit value to show on the eligible banner (e.g. 1.0). */
  creditValue?: number;
  /** Disable the download button (e.g. record not yet generated). */
  downloadDisabled?: boolean;
  /** Handler for the download click. */
  onDownload?: () => void;
  /** Handler for the email-me click. */
  onEmail?: () => void;
  /** When eventId + email are provided, the widget self-fetches from
   *  /events/:id/certificate/progress and overrides the prop values with
   *  backend data once the certificate record exists (post-event). During
   *  the live meeting (before End Webinar), the widget falls back to the
   *  passed-in props because the certificate record isn't created yet. */
  eventId?: number | string;
  email?: string;
}

/**
 * Compact attendee-side widget showing CPE/SHRM progress.
 * Presentational only — parent computes attendedMinutes/pollMarkers.
 */
const CpeProgressWidget: React.FC<CpeProgressWidgetProps> = ({
  certificateType: typeProp = "cpe",
  attendedMinutes: attendedMinutesProp,
  requiredMinutes: requiredMinutesProp,
  pollMarkers = [],
  pollsRequired,
  isLive = false,
  eligible: eligibleProp,
  creditValue: creditValueProp,
  downloadDisabled: downloadDisabledProp,
  onDownload: onDownloadProp,
  onEmail: onEmailProp,
  eventId,
  email,
}) => {
  // Backend-fetched progress (used when eventId+email provided and a
  // certificate_records row exists for this attendee — typically post-event).
  const backend = useBackendProgress(eventId, email, isLive);

  // Backend is the source of truth whenever it has answered — it live-computes
  // attendance, polls, and eligibility against the current event config.
  const hasBackend = !!backend.data;
  const certificateType: CpeProgressType =
    (backend.data?.certificateType === "shrm" ? "shrm" : undefined) ||
    (backend.data?.certificateType === "cpe" ? "cpe" : undefined) ||
    typeProp;
  const attendedMinutes = hasBackend
    ? backend.data!.attendedMinutes
    : attendedMinutesProp;
  const requiredMinutes =
    backend.data?.requiredMinutes && backend.data.requiredMinutes > 0
      ? backend.data.requiredMinutes
      : requiredMinutesProp;
  const eligible: boolean | undefined = hasBackend
    ? backend.data!.eligible ?? undefined
    : eligibleProp;
  const creditValue =
    backend.data?.creditValue != null
      ? backend.data.creditValue
      : creditValueProp;

  const safeRequired = requiredMinutes > 0 ? requiredMinutes : 1;
  const attendancePct = Math.min(100, (attendedMinutes / safeRequired) * 100);
  const attendanceMet = attendedMinutes >= requiredMinutes;

  // Per-poll markers come from the backend whenever it has them; otherwise
  // fall back to the props the dashboard supplies.
  const backendMarkers = backend.data?.pollMarkers ?? [];
  const effectiveMarkers: CpeProgressPollMarker[] =
    backendMarkers.length > 0
      ? backendMarkers.map((m) => ({
          pollId: m.pollId,
          sequence: m.sequence,
          answered: m.answered,
        }))
      : pollMarkers;
  const totalPolls = certificateType === "shrm" ? 0 : effectiveMarkers.length;
  const answeredCount = effectiveMarkers.filter((p) => p.answered).length;
  const hasBackendRecord = !!backend.data?.status;
  const pollsTarget =
    certificateType === "shrm" ? 0 : typeof pollsRequired === "number" ? pollsRequired : totalPolls;
  const pollsPct =
    pollsTarget > 0 ? Math.min(100, (answeredCount / pollsTarget) * 100) : 0;
  const pollsMet = certificateType === "shrm" ? true : answeredCount >= pollsTarget;

  const shrmMarkers = useMemo(() => {
    if (certificateType !== "shrm") return [];
    return [15, 30, 45, 60].map((min) => ({
      pollId: `shrm-${min}`,
      sequence: min,
      offsetFraction: min / 60,
      answered: attendedMinutes >= min,
      missed: false,
    }));
  }, [certificateType, attendedMinutes]);

  // Effective live state. The dashboard's `isLive` prop is per-browser and
  // can go stale (e.g., one tab fetched eventData before the meeting flipped
  // to live, never refetched → that tab sees `isLive=false` while the meeting
  // is actually running, while another attendee sees the correct state).
  // The backend's `meetingLive` flag is derived from event.liveStartedAt +
  // !event.liveEndedAt, so it's authoritative for everyone. Prefer it when
  // backend data has landed; fall back to the prop only during cold start.
  const effectiveLive =
    typeof backend.data?.meetingLive === "boolean"
      ? backend.data.meetingLive
      : isLive;

  // Determine which "state" to render (live / eligible / not-eligible).
  const phase: "live" | "eligible" | "not_eligible" = useMemo(() => {
    if (effectiveLive) return "live";
    if (eligible === true) return "eligible";
    if (eligible === false) return "not_eligible";
    return "live";
  }, [effectiveLive, eligible]);

  const badgeText = certificateType === "shrm" ? "SHRM" : "NASBA";

  // Auto-wire download + email handlers when eventId+email present.
  const onDownload =
    onDownloadProp ||
    (eventId && email && backend.token
      ? () => {
          window.open(
            certificateFileUrl(eventId, email, backend.token!),
            "_blank",
            "noopener,noreferrer",
          );
        }
      : undefined);

  const [emailing, setEmailing] = useState(false);
  const onEmail =
    onEmailProp ||
    (eventId && email && backend.token
      ? async () => {
          if (emailing) return;
          setEmailing(true);
          try {
            await emailCertificate(eventId, email, backend.token!);
            backend.refetch();
          } catch {
            /* swallow — user can retry */
          } finally {
            setEmailing(false);
          }
        }
      : undefined);

  const allConditionsMet = attendanceMet && pollsMet;
  // Backend computes eligibility live (against current attendance + polls)
  // and exposes `downloadAvailable`. Prefer it whenever backend data has
  // landed; fall back to the locally-computed conditions only while the
  // first /progress fetch is still in flight.
  const downloadDisabled =
    downloadDisabledProp ??
    (backend.data
      ? !backend.data.downloadAvailable
      : !allConditionsMet);

  // Defense-in-depth: even though the dashboard already gates on
  // eventData.certificateEnabled, the backend is the source of truth — if
  // it ever reports the cert as disabled (e.g. admin flipped it off
  // mid-session) the widget hides itself instead of showing empty stats.
  if (
    backend.data &&
    (!backend.data.certificateEnabled ||
      backend.data.certificateType === "none")
  ) {
    return null;
  }

  const titleText =
    certificateType === "shrm" ? "Your SHRM Progress" : "Your CPE Progress";

  return (
    <div
      className="cpe-progress-wrapper"
      role="region"
      aria-label="Your certificate progress"
    >
      <div className="cpe-progress-header">
        <span className="cpe-title">{titleText}</span>
        <span className="cpe-badge">{badgeText}</span>
      </div>

      <div className="cpe-stats">
        <div className={`cpe-stat ${attendanceMet ? "is-met" : ""}`}>
          <div className="cpe-stat-label">Attendance</div>
          <div className="cpe-stat-value">
            {attendedMinutes}
            <span className="cpe-stat-target"> / {requiredMinutes} min</span>
          </div>
          <div
            className="cpe-stat-bar"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={requiredMinutes}
            aria-valuenow={attendedMinutes}
          >
            <i style={{ width: `${attendancePct}%` }} />
          </div>
        </div>

        {certificateType !== "shrm" && (
          <div className={`cpe-stat ${pollsMet ? "is-met" : ""}`}>
            <div className="cpe-stat-label">Polls</div>
            <div className="cpe-stat-value">
              {answeredCount}
              <span className="cpe-stat-target"> / {pollsTarget}</span>
            </div>
            <div
              className="cpe-stat-bar"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={pollsTarget}
              aria-valuenow={answeredCount}
            >
              <i style={{ width: `${pollsPct}%` }} />
            </div>
          </div>
        )}
      </div>

      {(totalPolls > 0 || certificateType === "shrm") && (
        <div className="cpe-tl-wrap">
          <div className="cpe-tl-head">
            <span>Timeline</span>
            <span>{timelineStatus(phase, attendedMinutes, requiredMinutes, certificateType, pollsMet)}</span>
          </div>
          <div className="cpe-timeline" aria-hidden="true">
            <div className="cpe-track" />
            <div
              className="cpe-progress-fill"
              style={{ width: `calc(${certificateType === "shrm" ? Math.min(100, (attendedMinutes / 60) * 100) : attendancePct}% - 6px)` }}
            />
            {(certificateType === "shrm" ? shrmMarkers : effectiveMarkers).map((m, idx) => {
              const fraction =
                typeof m.offsetFraction === "number"
                  ? m.offsetFraction
                  : (idx + 0.5) / totalPolls;
              const leftPct = Math.max(2, Math.min(98, fraction * 100));
              const cls = m.answered
                ? "is-answered"
                : m.missed
                ? "is-missed"
                : "";
              return (
                <React.Fragment key={m.pollId || idx}>
                  <div
                    className={`cpe-poll ${cls}`}
                    style={{ left: `${leftPct}%` }}
                    aria-label={
                      certificateType === "shrm"
                        ? `${m.sequence} mins ${m.answered ? "completed" : "upcoming"}`
                        : `Poll ${m.sequence ?? idx + 1} ${
                            m.answered
                              ? "answered"
                              : m.missed
                              ? "missed"
                              : "upcoming"
                          }`
                    }
                  />
                  <div
                    className="cpe-poll-label"
                    style={{ left: `${leftPct}%` }}
                  >
                    {certificateType === "shrm" ? `${m.sequence}m` : `P${m.sequence ?? idx + 1}`}
                  </div>
                </React.Fragment>
              );
            })}
            {effectiveLive && (
              <div
                className="cpe-playhead"
                style={{ left: `${certificateType === "shrm" ? Math.min(100, (attendedMinutes / 60) * 100) : attendancePct}%` }}
              />
            )}
          </div>
        </div>
      )}

      {phase === "live" && (
        <div
          className={`cpe-banner ${
            attendanceMet && pollsMet ? "is-ok" : "is-info"
          }`}
        >
          <span className="cpe-banner-icon">
            {attendanceMet && pollsMet ? "✓" : "i"}
          </span>
          {attendanceMet && pollsMet ? (
            <span>
              <b>You're eligible.</b>{" "}
              {certificateType === "shrm"
                ? "Your certificate will be emailed after the event."
                : "Download your certificate now."}
            </span>
          ) : !attendanceMet ? (
            <span>
              <b>Almost there.</b> {Math.max(0, requiredMinutes - attendedMinutes)}{" "}
              more minute{requiredMinutes - attendedMinutes === 1 ? "" : "s"} to
              earn your {certificateType === "shrm" ? "SHRM credit" : "CPE"}.
            </span>
          ) : (
            <span>
              <b>Keep going.</b> Answer{" "}
              {Math.max(0, pollsTarget - answeredCount)} more poll
              {pollsTarget - answeredCount === 1 ? "" : "s"} to stay eligible.
            </span>
          )}
        </div>
      )}

      {phase === "eligible" && (
        <div className="cpe-banner is-ok">
          <span className="cpe-banner-icon">✓</span>
          <span>
            <b>You're eligible.</b>{" "}
            {typeof creditValue === "number"
              ? `${creditValue} ${
                  certificateType === "shrm" ? "PDC" : "CPE credit"
                }${creditValue === 1 ? "" : "s"} earned.`
              : "Your certificate is ready."}
          </span>
        </div>
      )}

      {phase === "not_eligible" && (
        <div className="cpe-banner is-warn">
          <span className="cpe-banner-icon">!</span>
          <span>
            Attended {attendedMinutes} of {requiredMinutes} minutes. Certificate
            not issued.
          </span>
        </div>
      )}

      {/* SHRM: download/email CTAs are hidden — eligibility is incremental
          (0.25 PDC per 15 min), so a mid-webinar download would show an
          intermediate credit value that doesn't match the final cert. The
          attendee receives the certificate by email at end-of-webinar. */}
      {certificateType !== "shrm" &&
        (phase === "eligible" || phase === "live") && (
          <div className="cpe-cta-row">
            <button
              type="button"
              className="cpe-btn is-primary"
              disabled={downloadDisabled === true || !onDownload}
              onClick={onDownload}
            >
              Download Certificate
            </button>
            {phase === "eligible" && onEmail && (
              <button
                type="button"
                className="cpe-btn is-ghost"
                onClick={onEmail}
              >
                Email me
              </button>
            )}
          </div>
        )}
    </div>
  );
};

/**
 * Right-hand label on the timeline. Prefers showing how much time is left
 * until the attendee qualifies; falls back to "Complete" once the attendance
 * threshold is met or the event has ended.
 */
function timelineStatus(
  phase: "live" | "eligible" | "not_eligible",
  attendedMinutes: number,
  requiredMinutes: number,
  certificateType: CpeProgressType,
  pollsMet: boolean,
): string {
  const remaining = Math.max(0, requiredMinutes - attendedMinutes);
  if (phase === "live") {
    if (remaining <= 0) {
      {
      return certificateType === "shrm" 
        ? "Eligible" 
        : pollsMet ? "Eligible — download now" : "Answer polls to qualify";
    }
    }
    if (remaining === 1) return "1 min to go";
    return `${remaining} min to go`;
  }
  if (phase === "eligible") return "Complete";
  return "Webinar ended";
}

export default CpeProgressWidget;
